import { useEffect, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router'
import axios from 'axios'
import './OrderList.css'
import { orderType } from '../types'
import { DatePicker, getRepCodeName, StatusChip } from '../helper'
import {
  Button,
  Chip,
  Grid,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Skeleton,
  Autocomplete,
} from '@mui/material'
import { ContentCopy, EditOutlined, PauseCircleOutline } from '@mui/icons-material'
import dayjs from 'dayjs'
import ErrorScreen from '../Misc/ErrorScreen'
import { useRepCodes, useUser } from '../hooks'

const COLUMN_WIDTHS = {
  quoteNum: undefined,
  customerPo: undefined,
  repCode: undefined,
  salesOrderNumber: '8%',
  grossMargin: '8%',
  total: '10%',
  submittedDate: '15%',
  status: '11%',
  correction: '100px',
}

function OrderList() {
  const user = useUser()
  const [repCodes, _setRepCodes] = useRepCodes()
  const navigate = useNavigate()

  const [orders, setOrders] = useState<orderType[]>([])
  const [err, setErr] = useState(false)
  const [timerHelper, setTimerHelper] = useState(false)
  const [rowCount, setRowCount] = useState(0)
  const [filters, setFilters] = useState({
    quoteNum: '',
    customerPo: '',
    repCode: '',
    salesOrderNumber: '',
    dateSubmittedFrom: null as Date | null,
    dateSubmittedTo: null as Date | null,
    status: '',
    page: 0,
    count: 25,
    sort: '',
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    document.title = 'Order Lookup | Order Portal'

    const interval = setInterval(() => {
      setTimerHelper(prev => !prev)
    }, 60000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!user.email) {
      return
    }

    handleSearch(filters)
  }, [timerHelper, user.email])

  const handleSearch = (filterObj: typeof filters) => {
    setErr(false)
    setLoading(true)

    axios
      .get('/orders', {
        params: {
          quoteNum: filterObj.quoteNum,
          customerPO: filterObj.customerPo,
          repCode: filterObj.repCode,
          salesOrder: filterObj.salesOrderNumber,
          dateSubmittedFrom: filterObj.dateSubmittedFrom
            ? dayjs.utc(filterObj.dateSubmittedFrom).format('YYYY-MM-DD')
            : null,
          dateSubmittedTo: filterObj.dateSubmittedTo ? dayjs.utc(filterObj.dateSubmittedTo).format('YYYY-MM-DD') : null,
          status: filterObj.status,
          sort: filterObj.sort,
          count: filterObj.count,
          page: filterObj.page,
          userEmail: user.sales && !user.operations && !user.admin ? user.email : undefined,
        },
      })
      .then(response => {
        setOrders(response.data.orders)
        setRowCount(response.data.total)
      })
      .catch(err => {
        if (err.response) {
          // received error code other than 2xx
          console.error(err.response)
        } else if (err.request) {
          // no response received
          console.error(err.request)
        } else {
          // request was not made
          console.error(err.message)
          setErr(err.message)
        }
        setErr(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <div className="order-list">
      <div className="order-list-filters">
        <Grid container spacing="24px" className="order-list-filter-row">
          <Grid size={2}>
            <TextField
              fullWidth
              size="small"
              label="Quote #"
              value={filters.quoteNum}
              onChange={e => setFilters({ ...filters, quoteNum: e.target.value })}
            />
          </Grid>
          <Grid size={2}>
            <TextField
              fullWidth
              size="small"
              label="Customer PO"
              value={filters.customerPo}
              onChange={e => setFilters({ ...filters, customerPo: e.target.value })}
            />
          </Grid>
          <Grid size={2}>
            <Autocomplete
              fullWidth
              renderInput={params => <TextField {...params} label="Rep Code" />}
              size="small"
              onChange={(_, value) => setFilters({ ...filters, repCode: value?.RepCode ?? '' })}
              options={repCodes}
              getOptionLabel={option => `${option.RepCode} ${getRepCodeName(option)}`}
              value={repCodes.find(repCode => repCode.RepCode === filters.repCode) ?? null}
            />
          </Grid>
          <Grid size={1.5}>
            <TextField
              fullWidth
              size="small"
              label="S.O. #"
              value={filters.salesOrderNumber}
              onChange={e => setFilters({ ...filters, salesOrderNumber: e.target.value })}
            />
          </Grid>
          <Grid size={1.25}>
            <DatePicker
              size="small"
              label="Date From"
              value={filters.dateSubmittedFrom ? dayjs(filters.dateSubmittedFrom) : null}
              onChange={e => setFilters({ ...filters, dateSubmittedFrom: e?.toDate() ?? null })}
            />
          </Grid>
          <Grid size={1.25}>
            <DatePicker
              size="small"
              label="Date To"
              value={filters.dateSubmittedTo ? dayjs(filters.dateSubmittedTo) : null}
              onChange={e => setFilters({ ...filters, dateSubmittedTo: e?.toDate() ?? null })}
            />
          </Grid>
          <Grid size={2}>
            <TextField
              fullWidth
              select
              size="small"
              label="Status"
              value={filters.status}
              onChange={e => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="Hold - Back Office">Hold - Back Office</MenuItem>
              <MenuItem value="Hold - Sales">Hold - Sales</MenuItem>
              <MenuItem value="Hold - SCRM">Hold - SCRM</MenuItem>
              <MenuItem value="Multiple Vendor Quotes">Multiple Vendor Quotes</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
              <MenuItem value="Canceled">Canceled</MenuItem>
              <MenuItem value="Urgent">Urgent</MenuItem>
            </TextField>
          </Grid>
        </Grid>
        <div className="order-list-filter-buttons">
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              setFilters(prev => ({
                ...prev,
                quoteNum: '',
                customerPo: '',
                repCode: '',
                salesOrderNumber: '',
                dateSubmittedFrom: null,
                dateSubmittedTo: null,
                status: '',
                page: 0,
                sort: '',
              }))
              handleSearch({
                quoteNum: '',
                customerPo: '',
                repCode: '',
                salesOrderNumber: '',
                dateSubmittedFrom: null,
                dateSubmittedTo: null,
                status: '',
                page: 0,
                count: filters.count,
                sort: '',
              })
            }}
          >
            Clear
          </Button>
          <Button variant="contained" size="large" onClick={() => handleSearch(filters)}>
            Search
          </Button>
        </div>
      </div>
      {err ? (
        <ErrorScreen
          title="Error"
          message="An error occurred while loading orders. Please try again or contact support if the problem persists."
        />
      ) : (
        <div className="order-list-table-container">
          <div className="order-list-table-header">
            <Typography variant="h6">Orders</Typography>
            <TablePagination
              component="div"
              count={rowCount}
              rowsPerPage={filters.count}
              page={filters.page}
              onRowsPerPageChange={e => {
                const newCount = parseInt(e.target.value)
                setFilters(prev => ({ ...prev, count: newCount }))
                handleSearch({ ...filters, count: newCount })
              }}
              onPageChange={(_, page) => {
                setFilters(prev => ({ ...prev, page: page }))
                handleSearch({ ...filters, page: page })
              }}
            />
          </div>
          <Table stickyHeader color="primary">
            <TableHead>
              <TableRow>
                <TableCell width={COLUMN_WIDTHS.quoteNum}>
                  <TableSortLabel
                    onClick={() => {
                      const newSort = 'QuoteNumber ' + (filters.sort === 'QuoteNumber asc' ? 'desc' : 'asc')
                      setFilters(prev => ({ ...prev, sort: newSort }))
                      handleSearch({ ...filters, sort: newSort })
                    }}
                    active={filters.sort.includes('QuoteNumber')}
                    direction={filters.sort.includes('desc') ? 'desc' : 'asc'}
                  >
                    Quote #
                  </TableSortLabel>
                </TableCell>
                <TableCell width={COLUMN_WIDTHS.customerPo}>
                  <TableSortLabel
                    onClick={() => {
                      const newSort = 'CustomerPO ' + (filters.sort === 'CustomerPO asc' ? 'desc' : 'asc')
                      setFilters(prev => ({ ...prev, sort: newSort }))
                      handleSearch({ ...filters, sort: newSort })
                    }}
                    active={filters.sort.includes('CustomerPO')}
                    direction={filters.sort.includes('desc') ? 'desc' : 'asc'}
                  >
                    Customer PO
                  </TableSortLabel>
                </TableCell>
                <TableCell width={COLUMN_WIDTHS.repCode}>
                  <TableSortLabel
                    onClick={() => {
                      const newSort = 'RepCode ' + (filters.sort === 'RepCode asc' ? 'desc' : 'asc')
                      setFilters(prev => ({ ...prev, sort: newSort }))
                      handleSearch({ ...filters, sort: newSort })
                    }}
                    active={filters.sort.includes('RepCode')}
                    direction={filters.sort.includes('desc') ? 'desc' : 'asc'}
                  >
                    Rep Code
                  </TableSortLabel>
                </TableCell>
                <TableCell width={COLUMN_WIDTHS.salesOrderNumber}>
                  <TableSortLabel
                    onClick={() => {
                      const newSort = 'SalesOrderNumber ' + (filters.sort === 'SalesOrderNumber asc' ? 'desc' : 'asc')
                      setFilters(prev => ({ ...prev, sort: newSort }))
                      handleSearch({ ...filters, sort: newSort })
                    }}
                    active={filters.sort.includes('SalesOrderNumber')}
                    direction={filters.sort.includes('desc') ? 'desc' : 'asc'}
                  >
                    Sales Order #
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" width={COLUMN_WIDTHS.grossMargin}>
                  <TableSortLabel
                    onClick={() => {
                      const newSort = 'GrossMargin ' + (filters.sort === 'GrossMargin asc' ? 'desc' : 'asc')
                      setFilters(prev => ({ ...prev, sort: newSort }))
                      handleSearch({ ...filters, sort: newSort })
                    }}
                    active={filters.sort.includes('GrossMargin')}
                    direction={filters.sort.includes('desc') ? 'desc' : 'asc'}
                  >
                    GP
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" width={COLUMN_WIDTHS.total}>
                  <TableSortLabel
                    onClick={() => {
                      const newSort = 'OrderTotal ' + (filters.sort === 'OrderTotal asc' ? 'desc' : 'asc')
                      setFilters(prev => ({ ...prev, sort: newSort }))
                      handleSearch({ ...filters, sort: newSort })
                    }}
                    active={filters.sort.includes('OrderTotal')}
                    direction={filters.sort.includes('desc') ? 'desc' : 'asc'}
                  >
                    Total
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" width={COLUMN_WIDTHS.submittedDate}>
                  <TableSortLabel
                    onClick={() => {
                      const newSort =
                        'SubmissionTimestamp ' + (filters.sort === 'SubmissionTimestamp asc' ? 'desc' : 'asc')
                      setFilters(prev => ({ ...prev, sort: newSort }))
                      handleSearch({ ...filters, sort: newSort })
                    }}
                    active={filters.sort.includes('SubmissionTimestamp')}
                    direction={filters.sort.includes('desc') ? 'desc' : 'asc'}
                  >
                    Submitted Date
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" width={COLUMN_WIDTHS.status}>
                  Status
                </TableCell>
                <TableCell align="right" width={COLUMN_WIDTHS.correction}>
                  Correction
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: filters.count }).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: 9 }).map(() => (
                        <TableCell align="center">
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : orders.map(order => (
                    <TableRow
                      key={order.Id}
                      sx={{
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                        '& .MuiTableCell-root': {
                          fontWeight: order.Status === 'Submitted' && !order.ScrmHold ? 'bold' : 'normal',
                        },
                      }}
                    >
                      <TableCell>
                        <div className="order-list-table-multiline-cell">
                          <MuiLink component={RouterLink} to={`/order/${order.Id}`}>
                            {order.QuoteNumber}
                          </MuiLink>
                          {order.Urgent && <Chip size="small" label="Urgent" color="error" variant="filled" />}
                          {order.IsCorrection && (
                            <Chip size="small" color="warning" variant="filled" label="Correction" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="order-list-table-multiline-cell">{order.CustomerPO}</div>
                      </TableCell>
                      <TableCell>
                        {order.RepCode} {getRepCodeName(repCodes.find(repCode => repCode.RepCode === order.RepCode))}
                      </TableCell>
                      <TableCell>{order.SalesOrderNumber}</TableCell>
                      <TableCell align="right">
                        <div className="order-list-table-multiline-cell right-align">
                          ${order.GrossMargin?.toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell align="right">
                        <div className="order-list-table-multiline-cell right-align">
                          ${order.OrderTotal?.toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell align="right">{new Date(order.SubmissionTimestamp ?? 0).toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <div className="order-list-table-multiline-cell status">
                          {order.Status !== 'Submitted' && (
                            <StatusChip status={(order.Status ?? '') + (order.HasCorrection ? ' - Corrected' : '')} />
                          )}
                          {order.Status === 'Submitted' && 'Submitted'}
                          {order.MultipleVendorQuotes && (
                            <Chip size="small" label="Multiple Vendor Quotes" color="warning" icon={<ContentCopy />} />
                          )}
                          {order.ScrmHold && (
                            <Chip size="small" label="SCRM Hold" color="secondary" icon={<PauseCircleOutline />} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          disabled={order.Status !== 'Rejected' || order.HasCorrection}
                          onClick={() => navigate(`/order/correction/${order.Id}`)}
                        >
                          <EditOutlined />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
          <div className="order-list-table-footer">
            <TablePagination
              component="div"
              count={rowCount}
              rowsPerPage={filters.count}
              page={filters.page}
              onRowsPerPageChange={e => {
                const newCount = parseInt(e.target.value)
                setFilters(prev => ({ ...prev, count: newCount }))
                handleSearch({ ...filters, count: newCount })
              }}
              onPageChange={(_, page) => {
                const newPage = page
                setFilters(prev => ({ ...prev, page: newPage }))
                handleSearch({ ...filters, page: newPage })
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default OrderList
